import React, { useState, useEffect, useReducer, useRef } from "react";
import {
  Badge,
  Button,
  Descriptions,
  Form,
  Input,
  message,
  Modal,
  Select,
} from "antd";
import { db } from "../../Firebase/FirebaseConnection";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import "./BookShelvesMap.css";

const { Search } = Input;
const { Option } = Select;

const LibraryMap = () => {
  const [bookshelves, setBookshelves] = useState([]);
  const [selectedShelf, setSelectedShelf] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedShelf, setHighlightedShelf] = useState(null);
  const [footstepPath, setFootstepPath] = useState([]);
  const bookRefs = useRef({});
  const [availableShelves, setAvailableShelves] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [bookModalVisible, setBookModalVisible] = useState(false);
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const openBookModal = (book) => {
    setSelectedBook(book);
    setBookModalVisible(true);
  };

  const handleStartReading = () => {
    setBookModalVisible(false);
    setFormModalVisible(true);
    form.setFieldsValue({
      bookTitle: selectedBook?.title || "",
    });
  };

  const checkStudentNumber = async (studentNumber) => {
    if (!studentNumber) return;

    try {
      const sessionsRef = collection(db, "reading_sessions");
      const q = query(
        sessionsRef,
        where("studentNumber", "==", studentNumber),
        orderBy("timestamp", "desc"),
        limit(1)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const lastSession = querySnapshot.docs[0].data();
        form.setFieldsValue({
          fullName: lastSession.fullName || "",
          department: lastSession.department || "BSIT",
          section: lastSession.section || "",
          yearLevel: lastSession.yearLevel || "",
        });
      } else {
        console.log("No previous session found for this student.");
      }
    } catch (error) {
      console.error("Error fetching student details:", error);
    }
  };

  const handleConfirmReading = async (values) => {
    if (!selectedBook) return;

    const readingSession = {
      bookTitle: selectedBook.title,
      ...values,
      status: "Reading",
      timestamp: serverTimestamp(),
    };

    try {
      const sessionRef = doc(collection(db, "reading_sessions"));
      await setDoc(sessionRef, readingSession);
      message("Reading session started successfully!");

      setFormModalVisible(false);
      setSelectedBook(null);
      form.resetFields();
    } catch (error) {
      console.error("Error starting reading session:", error);
      alert("Failed to start reading session.");
    }
  };

  useEffect(() => {
    const fetchBooks = async () => {
      try {
        const booksCollection = await getDocs(collection(db, "books"));
        const booksData = booksCollection.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          copiesAvailable: parseInt(doc.data().copiesAvailable, 10),
          layerNumber: parseInt(doc.data().layerNumber, 10),
          shelfLocation: parseInt(doc.data().shelfLocation, 10),
          totalCopies: parseInt(doc.data().totalCopies, 10),
          status: doc.data().copiesAvailable > 0 ? "Available" : "Unavailable",
          color: getRandomColor(),
          department: doc.data().department || [],
        }));

        const shelfStructure = buildShelfStructure(booksData);
        setBookshelves(shelfStructure);

        const uniqueShelvesFromBooks = Array.from(
          new Set(booksData.map((book) => book.shelfLocation))
        ).map((shelfId) => ({
          id: shelfId,
          label: `Shelf ${shelfId}`,
        }));

        setAvailableShelves(uniqueShelvesFromBooks);
      } catch (error) {
        console.error("Error fetching books:", error);
      }
    };

    fetchBooks();
  }, []);

  const getRandomColor = () => {
    const colors = ["#4682b4", "#ff6347", "#32cd32", "#ff8c00", "#9370db"];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const buildShelfStructure = (books) => {
    const shelfNameMapping = {
      0: "Outdated Shelves",
      2: "Shelf 2",
      3: "Shelf 3",
      4: "Shelf 4",
      5: "Shelf 5",
      6: "Shelf 6",
      7: "Shelf 7",
      8: "Shelf 8",
      9: "Shelf 9",
      10: "Shelf 10",
      11: "Shelf 11",
      12: "Shelf 12",
      13: "Shelf 13",
      1: "Shelf 1",
    };

    const shelfPositions = {
      0: { row: 1, col: 2, span: 3, type: "horizontal" },
      7: { row: 1, col: 5, span: 3, type: "horizontal" },
      3: { row: 2, col: 3, type: "vertical" },
      1: { row: 2, col: 5, type: "vertical" },
      4: { row: 2, col: 2, type: "vertical" },
      5: { row: 3, col: 6, type: "vertical" },
      6: { row: 2, col: 6, type: "vertical" },
      2: { row: 2, col: 4, type: "vertical" },
      8: { row: 2, col: 1, type: "vertical" },
      9: { row: 3, col: 1, type: "vertical" },
      10: { row: 4, col: 1, type: "vertical" },
      11: { row: 5, col: 1, type: "vertical" },
      12: { row: 5, col: 2, type: "horizontal" },
      13: { row: 4, col: 6, type: "vertical" },
    };

    return Object.keys(shelfPositions).map((id) => {
      const shelfId = Number(id);

      const shelfBooks = books.filter(
        (book) => Number(book.shelfLocation) === shelfId
      );

      return {
        id: shelfId,
        name: shelfNameMapping[shelfId] || `Shelf ${shelfId}`,
        row: shelfPositions[shelfId].row,
        col: shelfPositions[shelfId].col,
        type: shelfPositions[shelfId].type,
        books: shelfBooks,
        layers: [...new Set(shelfBooks.map((book) => book.layerNumber))].sort(
          (a, b) => a - b
        ),
      };
    });
  };

  const smoothScrollTo = (element, target, duration = 500) => {
    const start = element.scrollLeft;
    const distance = target - start;
    const startTime = performance.now();

    const animateScroll = (currentTime) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      const easeInOutQuad =
        progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      element.scrollLeft = start + distance * easeInOutQuad;

      if (elapsedTime < duration) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  };

  const scrollToSearchedBook = () => {
    if (!searchQuery) return;

    setTimeout(() => {
      const foundBook = bookshelves
        .flatMap((shelf) =>
          shelf.books.map((book) => ({ ...book, shelfId: shelf.id }))
        )
        .find((book) => {
          const searchValue = searchQuery.toLowerCase();
          return (
            book.title.toLowerCase().includes(searchValue) ||
            (book.author && book.author.toLowerCase().includes(searchValue)) ||
            (book.category &&
              (Array.isArray(book.category)
                ? book.category.some(
                    (cat) =>
                      typeof cat === "string" &&
                      cat.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                : typeof book.category === "string" &&
                  book.category
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()))) ||
            (book.bookDescription &&
              book.bookDescription.toLowerCase().includes(searchValue)) ||
            (book.isbn && book.isbn.toLowerCase().includes(searchValue)) ||
            (book.department &&
              Array.isArray(book.department) &&
              book.department.some((dep) =>
                dep.toLowerCase().includes(searchValue)
              ))
          );
        });

      if (foundBook) {
        const bookElement = bookRefs.current[foundBook.id];

        if (bookElement) {
          const shelfLayerElement = bookElement.closest(".bookshelf-layer");
          if (shelfLayerElement) {
            const bookLeft = bookElement.offsetLeft;
            const shelfWidth = shelfLayerElement.clientWidth;
            const targetScroll = bookLeft - shelfWidth / 2;

            smoothScrollTo(shelfLayerElement, targetScroll, 700);
          }

          const modalElement = document.querySelector(".bookshelf-modal");
          if (modalElement) {
            const bookTop = bookElement.offsetTop;
            const modalHeight = modalElement.clientHeight;
            const targetScrollTop = bookTop - modalHeight / 2;

            modalElement.scrollTo({
              top: targetScrollTop,
              behavior: "smooth",
            });
          }
        }
      }
    }, 500);
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    setFootstepPath([]);
    setSelectedBook(null);

    if (!value || bookshelves.length === 0) {
      setHighlightedShelf(null);
      return;
    }

    const matchingShelves = bookshelves
      .filter((shelf) =>
        shelf.books.some((book) => {
          const searchValue = value.toLowerCase();
          return (
            book.title.toLowerCase().includes(searchValue) ||
            (book.author && book.author.toLowerCase().includes(searchValue)) ||
            (book.category &&
              (Array.isArray(book.category)
                ? book.category.some((cat) =>
                    cat.toLowerCase().includes(searchValue)
                  )
                : book.category.toLowerCase().includes(searchValue))) ||
            (book.bookDescription &&
              book.bookDescription.toLowerCase().includes(searchValue)) ||
            (book.isbn && book.isbn.toLowerCase().includes(searchValue)) ||
            (book.department &&
              Array.isArray(book.department) &&
              book.department.some((dep) =>
                dep.toLowerCase().includes(searchValue)
              ))
          );
        })
      )
      .map((shelf) => shelf.id);

    if (matchingShelves.length > 0) {
      setHighlightedShelf(matchingShelves);
    } else {
      setHighlightedShelf(null);
    }
  };

  return (
    <div className="library-container">
      <div className="search-bookShelves">
        <Select
          size="large"
          className="bookshelf-dd"
          dropdownClassName="bookshelf-dropdownn"
          dropdownRender={(menu) => (
            <div className="bookshelf-dropdown">{menu}</div>
          )}
          placeholder="Select a shelf"
          style={{ width: 420 }}
          onChange={(shelfId) => {
            const shelf = bookshelves.find((s) => s.id === shelfId);
            if (shelf) {
              setSelectedShelf(shelf);
            }
          }}
        >
          {availableShelves.map((shelf) => (
            <Select.Option key={shelf.id} value={shelf.id}>
              {shelf.label}
            </Select.Option>
          ))}
        </Select>

        <Search
          placeholder="Search book title or author here..."
          allowClear
          enterButton="Search"
          size="large"
          className="custom-search"
          onSearch={handleSearch}
        />
      </div>

      <div className="library-grid">
        {bookshelves.map((shelf) => (
          <div
            key={shelf.id}
            className={`shelf ${shelf.type} ${
              (Array.isArray(highlightedShelf) &&
                highlightedShelf.includes(shelf.id)) ||
              highlightedShelf === shelf.id
                ? "highlighted"
                : ""
            }`}
            style={{
              gridRow: shelf.row,
              gridColumn: shelf.col,
              zIndex: 10,
            }}
            onClick={() => {
              setSelectedShelf(shelf);
              setTimeout(() => scrollToSearchedBook(), 500);
            }}
          >
            <div className="shelf-text">
              {shelf.name}
              {(Array.isArray(highlightedShelf) &&
                highlightedShelf.includes(shelf.id)) ||
              highlightedShelf === shelf.id ? (
                <span className="arrow">👆</span>
              ) : null}
            </div>
          </div>
        ))}

        <div className="library-entrance">📍 Entrance</div>
      </div>

      {selectedShelf && (
        <div className="modal-overlay" onClick={() => setSelectedShelf(null)}>
          <div className="bookshelf-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{selectedShelf.name}</h3>
            <p className="shelf-stats">
              Total Books on this Shelf: {selectedShelf.books.length}
            </p>

            <div className="bookshelf-layers">
              {selectedShelf?.books.length > 0 &&
                [
                  ...new Set(
                    selectedShelf.books.map((book) => book.layerNumber)
                  ),
                ]
                  .sort((a, b) => a - b)
                  .map((layer) => (
                    <div
                      key={layer}
                      className={`bookshelf-layer ${
                        searchQuery &&
                        selectedShelf?.books.some(
                          (b) =>
                            b.layerNumber === layer &&
                            b.title
                              .toLowerCase()
                              .includes(searchQuery.toLowerCase())
                        )
                          ? "highlighted-layer"
                          : ""
                      }`}
                    >
                      <h4 className="shelf-layer-title">Layer {layer}</h4>
                      <div className="layer-books">
                        {selectedShelf?.books
                          .filter((book) => book.layerNumber === layer)
                          .map((book) => (
                            <div
                              key={book.id}
                              ref={(el) => {
                                if (el) bookRefs.current[book.id] = el;
                              }}
                              className={`book-cover ${
                                searchQuery &&
                                (book.title
                                  .toLowerCase()
                                  .includes(searchQuery.toLowerCase()) ||
                                  String(book.department)
                                    ?.toLowerCase()
                                    .includes(searchQuery.toLowerCase()) ||
                                  book.author
                                    ?.toLowerCase()
                                    .includes(searchQuery.toLowerCase()) ||
                                  book.isbn
                                    ?.toLowerCase()
                                    .includes(searchQuery.toLowerCase()) ||
                                  book.bookDescription
                                    ?.toLowerCase()
                                    .includes(searchQuery.toLowerCase()) ||
                                  (Array.isArray(book.category)
                                    ? book.category.some((cat) =>
                                        cat
                                          .toLowerCase()
                                          .includes(searchQuery.toLowerCase())
                                      )
                                    : book.category
                                        ?.toLowerCase()
                                        .includes(searchQuery.toLowerCase())))
                                  ? "highlighted-book"
                                  : ""
                              }`}
                              style={{ backgroundColor: book.color }}
                              onClick={() => setSelectedBook(book)}
                            >
                              <span className="book-title">{book.title}</span>
                              {searchQuery &&
                                (book.title
                                  .toLowerCase()
                                  .includes(searchQuery.toLowerCase()) ||
                                  String(book.department)
                                    ?.toLowerCase()
                                    .includes(searchQuery.toLowerCase()) ||
                                  book.author
                                    ?.toLowerCase()
                                    .includes(searchQuery.toLowerCase()) ||
                                  book.isbn
                                    ?.toLowerCase()
                                    .includes(searchQuery.toLowerCase()) ||
                                  book.bookDescription
                                    ?.toLowerCase()
                                    .includes(searchQuery.toLowerCase()) ||
                                  (typeof book.category === "string"
                                    ? book.category
                                        .toLowerCase()
                                        .includes(searchQuery.toLowerCase())
                                    : Array.isArray(book.category) &&
                                      book.category.some(
                                        (cat) =>
                                          typeof cat === "string" &&
                                          cat
                                            .toLowerCase()
                                            .includes(searchQuery.toLowerCase())
                                      ))) && (
                                  <span className="hand-pointer">👇</span>
                                )}
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
            </div>

            <button
              className="modal-close-button"
              onClick={() => setSelectedShelf(null)}
            >
              ✖
            </button>
          </div>
        </div>
      )}

      <Modal
        title="📖 Book Details"
        visible={!!selectedBook}
        onCancel={() => setSelectedBook(null)}
        footer={null}
        centered
        className="custom-modal"
      >
        {selectedBook && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Title">
              {selectedBook.title}
            </Descriptions.Item>
            <Descriptions.Item label="Author">
              {selectedBook.author}
            </Descriptions.Item>
            <Descriptions.Item label="Category">
              {selectedBook.category}
            </Descriptions.Item>
            <Descriptions.Item label="Publisher">
              {selectedBook.publisher}
            </Descriptions.Item>
            <Descriptions.Item label="ISBN">
              {selectedBook.isbn}
            </Descriptions.Item>
            <Descriptions.Item label="Year">
              {selectedBook.year}
            </Descriptions.Item>
            <Descriptions.Item label="Shelf Location">
              {selectedBook.shelfLocation}
            </Descriptions.Item>
            <Descriptions.Item label="Department">
              {selectedBook.department}
            </Descriptions.Item>
            <Descriptions.Item label="Copies Available">
              {selectedBook.copiesAvailable}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Badge
                status={
                  selectedBook.status === "Available" ? "success" : "error"
                }
                text={selectedBook.status}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Description">
              {selectedBook.bookDescription}
            </Descriptions.Item>
          </Descriptions>
        )}

        <div style={{ textAlign: "center", marginTop: 20 }}>
          <Button
            type="primary"
            onClick={handleStartReading}
            size="large"
            style={{ width: "100%" }}
          >
            Start Reading
          </Button>
        </div>
      </Modal>

      <Modal
        title="📝 Enter Student Details"
        visible={formModalVisible}
        onCancel={() => setFormModalVisible(false)}
        footer={null}
        className="custom-modal"
        centered
      >
        <Form form={form} layout="vertical" onFinish={handleConfirmReading}>
          <Form.Item
            label="Book Title"
            name="bookTitle"
            initialValue={selectedBook?.title || ""}
          >
            <Input placeholder="Book Title" readOnly />
          </Form.Item>
          <Form.Item
            label="Student Number"
            name="studentNumber"
            rules={[{ required: true, message: "Please enter student number" }]}
            onBlur={checkStudentNumber}
            onChange={(e) => checkStudentNumber(e.target.value)}
          >
            <Input placeholder="Enter student student number" />
          </Form.Item>
          <Form.Item
            label="Student Full Name"
            name="fullName"
            rules={[{ required: true, message: "Please enter your full name" }]}
          >
            <Input placeholder="Enter student full name" />
          </Form.Item>

          <Form.Item
            label="Student Department"
            name="department"
            initialValue="BSIT"
            rules={[
              { required: true, message: "Please enter student department" },
            ]}
          >
            <Input placeholder="Enter student department" />
          </Form.Item>

          <Form.Item
            label="Student Section"
            name="section"
            rules={[{ required: true, message: "Please enter your section" }]}
          >
            <Input placeholder="Enter your section" />
          </Form.Item>

          <Form.Item
            label="Student Year Level"
            name="yearLevel"
            rules={[
              { required: true, message: "Please enter your year level" },
            ]}
          >
            <Input placeholder="Enter your year level" />
          </Form.Item>

          {/* Confirm Reading Button */}
          <Form.Item>
            <Button type="primary" htmlType="submit" block size="large">
              ✅ Confirm Reading
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LibraryMap;
